/**
 * Modal — geteiltes Dialog-Primitive (Sprint UI-D2a).
 *
 * Portal nach `document.body`, drag-resistenter Backdrop, Esc-to-close,
 * Focus-Trap (Frisch-Abfrage pro Tab), tokenisierter Body-Scroll-Lock +
 * Modal-Stack (StrictMode-idempotent). `onClose` wird über eine Ref gelesen —
 * die Effekte hängen nur an `[open]`, ein instabiler Caller-`onClose`
 * verursacht keinen Focus-Loss (patterns/react-modal.md § handleClose-stable).
 *
 * Stapelung: Esc/Backdrop/Focus-Trap reagieren nur für das oberste Modal im
 * Stack. Gleicher `z-index`; die Portal-Mount-Reihenfolge regelt die Optik.
 *
 * `disableClose` sperrt ALLE Close-Pfade (X/Esc/Backdrop) — gedacht für
 * "kein Abbruch während eines pending Requests". `closeOnBackdrop={false}`
 * sperrt nur den Backdrop-Klick (Esc + X bleiben aktiv).
 */
import {
  useEffect,
  useId,
  useRef,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { I } from '../../lib/icons';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type ModalRole = 'dialog' | 'alertdialog';
export type ModalPlacement = 'center' | 'bottom-sheet';

export interface ModalProps {
  open: boolean;
  /** MUSS bei `disableClose`/Submitting-Guards mit `disableClose` kombiniert
   *  werden — `onClose` selbst wird vom Primitive ungeprüft aufgerufen. */
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  role?: ModalRole;
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Backdrop-Klick schließt (Default true). Esc + X bleiben unberührt. */
  closeOnBackdrop?: boolean;
  /** Sperrt X + Esc + Backdrop komplett (Default false). */
  disableClose?: boolean;
  describedById?: string;
  /** Docking — 'center' (Default) oder 'bottom-sheet' (Mobile unten angedockt,
   *  ab md: zentriert). Regelt NUR Docking/Radius/Breite/Animation. */
  placement?: ModalPlacement;
  /** Non-scrolling Slot im Header unter der Titel-Zeile — für Suchfelder /
   *  Subtitles, die nicht mit dem Body mitscrollen dürfen. */
  headerExtra?: ReactNode;
  /** Additiver Slot in der Titelzeile, links NEBEN dem X-Button (gleicher
   *  rechter Flex-Container) — z.B. ein Minimieren-Button. Default kein Slot,
   *  kein Verhaltens-Change für alle anderen Modals. */
  headerActions?: ReactNode;
  /** Body nutzt `overflow-visible` statt `overflow-y-auto` — für KURZE Modals
   *  mit absolut positioniertem Dropdown/Typeahead (z.B. RecipePicker), dessen
   *  Popover sonst am Body-Rand abgeschnitten würde. Nur verwenden, wenn der
   *  Inhalt sicher kleiner als die Panel-Höhe (max-h-[90vh]) ist. */
  overflowVisibleBody?: boolean;
}

// ── Modal-Stack — modul-level, tokenisiert, ownership-aware ──────────────
const modalStack: symbol[] = [];
function pushModal(token: symbol): void {
  if (!modalStack.includes(token)) modalStack.push(token);
}
function removeModal(token: symbol): void {
  const i = modalStack.indexOf(token);
  if (i !== -1) modalStack.splice(i, 1);
}
function isTopModal(token: symbol): boolean {
  return modalStack.length > 0 && modalStack[modalStack.length - 1] === token;
}

// ── Body-Scroll-Lock — tokenisiert, StrictMode-idempotent ───────────────
const scrollLockTokens = new Set<symbol>();
let savedBodyOverflow: string | null = null;
function lockBodyScroll(token: symbol): void {
  if (scrollLockTokens.size === 0) {
    savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLockTokens.add(token);
}
function unlockBodyScroll(token: symbol): void {
  if (!scrollLockTokens.has(token)) return; // idempotent gegen Doppel-Cleanup
  scrollLockTokens.delete(token);
  if (scrollLockTokens.size === 0 && savedBodyOverflow !== null) {
    document.body.style.overflow = savedBodyOverflow;
    savedBodyOverflow = null;
  }
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), ' +
  'select:not([disabled]), textarea:not([disabled]), ' +
  '[tabindex]:not([tabindex="-1"])';

/** Fokussierbare Elemente im Panel — bewusst KEIN offsetParent-Visibility-
 *  Check (jsdom berechnet kein Layout; würde alle Tests leerlaufen lassen). */
function getFocusable(panel: HTMLElement): HTMLElement[] {
  return Array.from(
    panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      el.getAttribute('aria-hidden') !== 'true',
  );
}

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
  // '2xl' (Quick-Look-Sprint): breites Panel für das zweispaltige Preview+Inspector-Overlay.
  '2xl': 'max-w-6xl',
};

// Bottom-Sheet: Mobile volle Breite, ab md: die size-Breite. Der Tailwind-
// Scanner braucht die `md:`-Klassen als Literale → eigene Map (kein Concat).
const SIZE_CLASS_SHEET_MD: Record<ModalSize, string> = {
  sm: 'md:max-w-md',
  md: 'md:max-w-lg',
  lg: 'md:max-w-2xl',
  xl: 'md:max-w-3xl',
  '2xl': 'md:max-w-6xl',
};

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  role = 'dialog',
  initialFocusRef,
  closeOnBackdrop = true,
  disableClose = false,
  describedById,
  placement = 'center',
  headerExtra,
  headerActions,
  overflowVisibleBody = false,
}: ModalProps) {
  const titleId = `gf-modal-title-${useId()}`;

  // Stabiles Token pro Modal-Instanz (Stack + Scroll-Lock-Identität).
  const tokenRef = useRef<symbol | null>(null);
  if (tokenRef.current === null) tokenRef.current = Symbol('gf-modal');
  const token = tokenRef.current;

  const panelRef = useRef<HTMLDivElement>(null);
  const mouseDownOnBackdropRef = useRef(false);

  // onClose + Guards via Ref → Effekte hängen nur an [open] (DK-9).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const disableCloseRef = useRef(disableClose);
  disableCloseRef.current = disableClose;

  // Stack + Scroll-Lock + Initial-Fokus + Focus-Return.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    pushModal(token);
    lockBodyScroll(token);

    const panel = panelRef.current;
    const focusTarget =
      initialFocusRef?.current ??
      (panel ? getFocusable(panel)[0] : null) ??
      panel;
    focusTarget?.focus();

    return () => {
      removeModal(token);
      unlockBodyScroll(token);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [open, token, initialFocusRef]);

  // Keydown: Esc (top-of-stack, Select-Guard, disableClose-Guard) + Focus-Trap.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isTopModal(token)) return;

      if (e.key === 'Escape') {
        if (disableCloseRef.current) return;
        // Offenes natives <select> fängt Esc selbst ab — nicht das Modal.
        const target = e.target as HTMLElement | null;
        if (target && target.tagName === 'SELECT') return;
        onCloseRef.current();
        return;
      }

      if (e.key === 'Tab') {
        const panel = panelRef.current;
        if (!panel) return;
        // Frisch-Abfrage pro Tab — Modals fügen nach Async-Loads Elemente hinzu.
        const focusables = getFocusable(panel);
        if (focusables.length === 0) {
          e.preventDefault();
          panel.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === first || !panel.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last || !panel.contains(active)) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, token]);

  if (!open) return null;

  const isSheet = placement === 'bottom-sheet';

  return createPortal(
    <div
      className={`gf-modal-backdrop fixed inset-0 z-50 flex justify-center bg-brand-green-900/40 ${
        isSheet ? 'items-end p-0 md:items-center md:p-4' : 'items-center p-4'
      }`}
      onMouseDown={(e) => {
        mouseDownOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        // Drag-resistent: NUR schließen wenn mousedown UND click beide auf
        // dem Backdrop selbst landen (patterns/react-modal.md).
        const onBackdrop =
          e.target === e.currentTarget && mouseDownOnBackdropRef.current;
        mouseDownOnBackdropRef.current = false;
        if (onBackdrop && !disableClose && closeOnBackdrop) onClose();
      }}
    >
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedById}
        tabIndex={-1}
        className={`${
          isSheet ? 'gf-modal-panel-sheet' : 'gf-modal-panel'
        } flex max-h-[90vh] w-full flex-col bg-white shadow-xl focus:outline-none ${
          isSheet
            ? `max-w-none rounded-t-2xl md:rounded-lg ${SIZE_CLASS_SHEET_MD[size]}`
            : `${SIZE_CLASS[size]} rounded-lg`
        }`}
      >
        <div className="flex items-start justify-between gap-4 px-6 pb-3 pt-5">
          <h2 id={titleId} className="text-lg font-semibold text-brand-green-700">
            {title}
          </h2>
          {/* Rechter Header-Cluster: optionale headerActions (z.B. Minimieren)
              links neben dem X-Button. */}
          <div className="-mr-1 -mt-1 flex shrink-0 items-center gap-1">
            {headerActions}
            <button
              type="button"
              onClick={onClose}
              disabled={disableClose}
              aria-label="Dialog schließen"
              className="shrink-0 rounded p-1 text-brand-green-600 transition-colors hover:bg-ink-50 hover:text-brand-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <I.close size={20} />
            </button>
          </div>
        </div>
        {headerExtra && <div className="px-6 pb-3">{headerExtra}</div>}
        <div className={`${overflowVisibleBody ? 'overflow-visible' : 'overflow-y-auto'} px-6 pb-1`}>
          {children}
        </div>
        {footer && (
          <div className="flex justify-end gap-2 px-6 pb-5 pt-4">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
